import { InfiniteQueryObserverResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { TranslationFeKey } from "shared/types/translation-key";

// import { OnMessageSendFunction } from "./conversation";
import { useConversationState } from "./conversation-state-provider";
// import { CompletionCTACard } from "../../answer/completion-cta-card";
// import { Suggestions } from "../../answer/suggestions";
import { PaginatedConversationRecordWithMessages } from "~/api/generated/multiagent";
import { useCurrentNode } from "~/components/app-shell/providers/current-note-provider";
import { getGtmRouteTarget } from "~/components/app-shell/providers/gtm-events-provider/events";
import { useSendGtmEvents } from "~/components/app-shell/providers/gtm-events-provider/gtm-events-provider";
import { ChatMessageComponent } from "~/components/chat/generic/chat-message";
import { ChatMessage, ChatRole } from "~/components/chat/types";
import { LoadingIndicator } from "~/components/loading-spinner";
import { Text } from "~/components/text";
import { cn } from "~/util/cn";
import { useIntersectionObserver } from "~/util/hooks";
import { useTenantId } from "~/util/use-active-tenant-id";

export type MessageListProps = {
  chatMessages: ChatMessage[];
  chatLoadingMessage?: TranslationFeKey;
  userChatColors?: { bubbleColor?: string; textColor?: string };
  fetchMoreMessages?: {
    fetchMoreMessagesfn: () => Promise<
      InfiniteQueryObserverResult<
        PaginatedConversationRecordWithMessages,
        unknown
      >
    >;
    isfetchingMoreMessages: boolean;
    hasMoreMessages: boolean;
  };
  shouldShowDisclaimer?: boolean;
  className?: string;
  initialScrollDelay?: number;
  // onSendMessage: OnMessageSendFunction;
};

export const MessageList: React.FC<MessageListProps> = ({
  chatMessages,
  chatLoadingMessage,
  fetchMoreMessages,
  shouldShowDisclaimer = false,
  className,
  initialScrollDelay = 0,
  // onSendMessage,
}) => {
  const scrollableRef = useRef<HTMLElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { isChatBusy } = useConversationState();
  const { sendTabEvent } = useSendGtmEvents();
  const tenantId = useTenantId();
  const location = useLocation();
  const lastSentPathnameRef = useRef<string | null>(null);
  const { node } = useCurrentNode();

  useEffect(() => {
    const target = getGtmRouteTarget(location.pathname);
    if (!target) {
      return;
    }

    if (lastSentPathnameRef.current === location.pathname) {
      return;
    }

    lastSentPathnameRef.current = location.pathname;

    sendTabEvent({
      eventName: "tab_view",
      target,
      bottomNavigation: window.innerWidth <= 1024,
    });
  }, [tenantId.disease, location.pathname, node, sendTabEvent]);

  const scrollToBottom = (smooth = true) => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({
        top: scrollableRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  const hasInitialScrollRef = useRef(false);

  const loadMoreRef = useRef(null);

  const loadMoreMessages = useIntersectionObserver(
    loadMoreRef,
    {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    },
    500,
  );

  useEffect(() => {
    if (
      !hasInitialScrollRef.current ||
      chatMessages.length === 0 ||
      loadMoreMessages
    )
      return;

    if (chatMessages[chatMessages.length - 1].role === ChatRole.USER)
      scrollToBottom(true);
  }, [chatMessages, loadMoreMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (loadMoreMessages && fetchMoreMessages?.hasMoreMessages) {
      fetchMoreMessages?.fetchMoreMessagesfn();
    }
  }, [fetchMoreMessages, loadMoreMessages]);

  useEffect(() => {
    if (hasInitialScrollRef.current) return;

    const initialScrollTimer = setTimeout(() => {
      scrollToBottom(true);
      hasInitialScrollRef.current = true;
    }, initialScrollDelay);

    return () => clearTimeout(initialScrollTimer);
  }, [initialScrollDelay]);

  // const userMessageNumber =
  //   chatMessages.filter((message) => message.role === ChatRole.USER).length ||
  //   0;

  return (
    <main
      ref={scrollableRef}
      className={cn(
        !shouldShowDisclaimer && "pb-20",
        "px-3 pt-9",
        "flex h-full flex-col justify-start gap-4 overflow-y-auto overflow-x-hidden sm:gap-6",
        "bg-gradient-to-b from-base-200/20 to-transparent",
      )}
    >
      {chatMessages.map((chatMessage, index) => (
        <div
          key={index}
          ref={index === chatMessages.length - 1 ? chatContainerRef : undefined}
          className={cn("flex w-full flex-col", className)}
        >
          {index === 0 && (
            <div
              ref={loadMoreRef}
              className="z-10 flex flex-row items-center justify-center px-3 md:h-16 md:py-5"
            >
              {fetchMoreMessages?.isfetchingMoreMessages && (
                <LoadingIndicator as="spinner" className="h-16 w-16" />
              )}
            </div>
          )}
          <ChatMessageComponent
            message={chatMessage}
            media={chatMessage.media}
          />
        </div>
      ))}
      {isChatBusy && (
        <div
          className={cn(
            "flex w-full max-w-screen-sm items-start justify-start pb-2 pt-4 animate-slideIn",
            className,
          )}
        >
          <span className="loading loading-ring loading-md mr-2" />
          <Text
            tx={chatLoadingMessage ?? "chat.loading"}
            className="text-sm text-mama-default-primary"
          />
        </div>
      )}
      {shouldShowDisclaimer ? (
        <Text
          as="p"
          tx="legal.mamaAskDisclaimer"
          className={cn(
            "mx-auto -mt-2 pb-9 pl-2 sm:-mt-4",
            "w-full max-w-screen-sm gap-1",
            "text-left text-xs font-normal text-slate-400",
            "transition-opacity ease-in-out",
            isChatBusy ? "opacity-0 duration-75" : "opacity-100 duration-500",
          )}
        />
      ) : null}
      {/* <CompletionCTACard userMessageNumber={userMessageNumber} />
      <Suggestions
        userMessageNumber={userMessageNumber}
        onSuggestionClick={(message) =>
          onSendMessage({ message, selection: [] })
        }
      /> */}
      <div ref={bottomRef} />
    </main>
  );
};
